import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { notificationService } from '@/services';
import { useHouseholdStore } from '@/stores';
import { createCoalescedRunner } from '@/lib/coalesce';

/**
 * Realtime unread notification count for the ACTIVE household.
 *
 * Resolves the public.users.id from auth.uid() on mount, then subscribes
 * to the `unread:<userId>` broadcast channel for live badge updates.
 *
 * The badge must count the same rows the Activity list shows, so it is
 * household-bound too (YAR-358). The broadcast channel stays keyed by user —
 * it is a "something changed, re-read" signal, and the re-read is scoped.
 */
export function useUnreadCount() {
  const [count, setCount] = useState(0);
  const [userId, setUserId] = useState<string | null>(null);
  const householdId = useHouseholdStore((s) => s.householdId);

  // Resolve public.users.id from auth user
  useEffect(() => {
    (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('users')
        .select('id')
        .eq('supabase_id', user.id)
        .maybeSingle();

      if (data) setUserId(data.id);
    })();
  }, []);

  // Fetch count + subscribe to broadcast
  useEffect(() => {
    if (!userId) return;
    if (!householdId) {
      setCount(0);
      return;
    }

    let active = true;
    // Monotonic: the mount read runs outside the coalescing runner, so it can
    // overlap a runner read. Only the most recently STARTED read may set the
    // badge — a slower, older response must not overwrite a newer count.
    let seq = 0;
    const refresh = () => {
      const mine = ++seq;
      return notificationService
        .getUnreadCount(householdId)
        .then((n) => {
          // A read that lands after a household switch must not overwrite
          // the new household's badge, nor may an older read.
          if (active && mine === seq) setCount(n);
        })
        .catch(console.error);
    };

    // The server broadcasts once per notification ROW, so one approval can
    // send dozens of signals in a burst. Coalesce them: one read after the
    // burst settles (≤3 s even under a continuous stream), never two reads
    // in flight at once — see lib/coalesce.ts.
    const runner = createCoalescedRunner(refresh, {
      wait: 1000,
      maxWait: 3000,
    });

    refresh();

    const channel = supabase
      .channel(`unread:${userId}`)
      // The payload's count is user-wide, so it cannot be trusted as the
      // badge value — it would re-introduce the cross-household total the
      // scoped fetch just removed. Treat the broadcast purely as an
      // invalidation and re-read within the active household.
      .on('broadcast', { event: 'unread_count' }, () => {
        runner.trigger();
      })
      .subscribe();

    return () => {
      active = false;
      runner.cancel();
      supabase.removeChannel(channel);
    };
  }, [userId, householdId]);

  return count;
}
